/* eslint-disable react/no-array-index-key */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGallery } from 'react-photoswipe-gallery';
import { PDPThumbnail, PlayVideo } from 'autogen/translation-keys/trans-website-imagealttext';
import { format } from 'modules/helpers/strings';
import {
    type GalleryMediaItem,
    type GalleryMediaItems,
    MediaItemDiscriminator,
    type onThumbnailClick
} from '../shared/types';
import ThumbnailGallery360Item from './Gallery360Item/components/ThumbnailGallery360Item';
import ThumbnailHighlightedVideoItem from './GalleryHighlightedVideoItem/components/ThumbnailHighlightedVideoItem';
import ThumbnailGalleryImageItem from './GalleryImageItem/components/ThumbnailGalleryImageItem';
import ThumbnailGalleryVideoItem from './GalleryVideoItem/components/ThumbnailGalleryVideoItem';

export default function GalleryThumbnailRenderer({
    items,
    onThumbnailHover,
    onThumbnailClick
}: {
    items: GalleryMediaItems;
    onThumbnailHover: (index: number) => void;
    onThumbnailClick?: onThumbnailClick;
}) {
    const { open: galleryOpener } = useGallery();
    const { t } = useTranslation();
    const [activeIndex, setActiveIndex] = useState<number>(0);

    function handleClick(index: number, type: GalleryMediaItem['type']) {
        galleryOpener(index);

        if (onThumbnailClick) {
            onThumbnailClick({ index, type });
            setActiveIndex(index);
        }
    }

    return (
        <>
            {items.map((item, index) =>
                MediaItemDiscriminator(
                    item,
                    (imageItemModel) => (
                        <ThumbnailGalleryImageItem
                            key={index}
                            imageItem={{ ...imageItemModel, alt: format(t(PDPThumbnail))(index + 1) }}
                            onMouseOver={() => onThumbnailHover(index)}
                            onClick={() => handleClick(index, 'image')}
                            isActive={activeIndex === index}
                        />
                    ),
                    (videoItemModel) => (
                        <ThumbnailGalleryVideoItem
                            key={index}
                            videoItem={videoItemModel}
                            onClick={() => handleClick(index, 'video')}
                            aria-label={t(PlayVideo)}
                            isActive={activeIndex === index}
                        />
                    ),
                    (highlightedVideoItemModel) => (
                        <ThumbnailHighlightedVideoItem
                            key={index}
                            highlightedVideoItem={highlightedVideoItemModel}
                            onMouseOver={() => onThumbnailHover(index)}
                            onClick={() => handleClick(index, 'highlightedVideo')}
                            aria-label={t(PlayVideo)}
                            isActive={activeIndex === index}
                        />
                    ),
                    (product360ItemModel) => (
                        <ThumbnailGallery360Item
                            key={index}
                            product360Item={product360ItemModel}
                            onMouseOver={() => onThumbnailHover(index)}
                            onClick={() => handleClick(index, 'product360')}
                            isActive={activeIndex === index}
                        />
                    ),
                    () => null
                )
            )}
        </>
    );
}
